/**
 * ENHANCED SKY at night, drawn: 500-odd catalogue stars as one mesh, and a moon
 * with a real terminator on it. `night-sky-look.ts` decides where everything
 * is; this file is the three.js half and holds no astronomy at all.
 *
 * ## Both meshes are built once and thereafter only moved
 *
 * `star-field.ts` records what happens when a sky layer rewrites its vertex
 * buffer inside `onBeforeRender` under this renderer — the attribute for that
 * pass has already been taken, so the buffer keeps frame zero's contents and
 * the layer draws nothing, silently. The fix it found is used again here: a
 * star is 18 km away and the camera is always near the dome's centre, so the
 * direction from the camera to a star *is* the star's own bearing, and a quad
 * built perpendicular to that bearing already faces the lens from anywhere the
 * aeroplane can get to. The geometry is therefore static and the mesh rides
 * the camera, which is the mechanism `world.ts`'s dome and `sky-dressing.ts`'s
 * disc both use and the one that is known to work here.
 *
 * The moon does have per-frame state — which way its lit limb points depends
 * on where the camera is looking — but that state is two floats in a **uniform**,
 * not a buffer, and a uniform written in `onBeforeRender` does land.
 *
 * ## Plain opaque depth, on both, deliberately
 *
 * `depthWrite: false` + `depthTest: false` is the pair that reads as obviously
 * right for a body at infinity and rasterises **nothing** under this renderer's
 * reversed-Z swapchain. `lessons.md` records four instances, two of them in
 * this very corner of the code: the sun/moon disc, which had never once been
 * drawn in `/fly`, and the first star field, whose draw call went out over a
 * peak red of 28 where its own palette entry carries 113. Neither mesh here
 * opts out of anything. Both sit at a real distance inside the far plane, both
 * have an honest depth to write, and writing it is what lets a hill or an
 * aeroplane in front of them occlude them.
 */

import * as THREE from "three/webgpu";
import { CAMERA_FOV_DEG } from "./flight-view.ts";
import { CELESTIAL_RENDER_ORDER, DISC_RADIUS, discGeometry, placeCelestialDisc } from "./celestial-disc.ts";
import { MOON_DISC } from "./time-of-day.ts";
import { mixPalette, type EnhancedSkyStops } from "./enhanced-sky-look.ts";
import {
  nightSkyFor,
  type NightSky,
  type NightSkyBrief,
  type PlacedMoon,
  type PlacedNightStar,
  limitMagnitudeFor,
} from "./night-sky-look.ts";
import { metresPerScreenPixel, metresPerScreenPixelX } from "../three/sprite-screen-size.ts";
import { quantiseToVgaDac } from "../three/ordered-dither.ts";
import { CELESTIAL_DISTANCE_M } from "./world.ts";

/** After the dome (`-1`), behind the moon (`CELESTIAL_RENDER_ORDER`) — a star is not drawn over the moon's disc. */
const STAR_RENDER_ORDER = -0.75;

/**
 * How wide the terminator's own gradient is, as a fraction of the disc's
 * radius. Not zero, because a hard step across a disc that is 4 degrees wide
 * on screen aliases into a staircase the moment the aeroplane rolls; not wide,
 * because the terminator on a gibbous moon is a *line* and softening it into a
 * band is how a moon starts looking like a lamp.
 */
const TERMINATOR_SOFTNESS = 0.035;

/**
 * The vertex buffers for one static star field. Every quad is built about its
 * own bearing, with a per-star size in original 200-line pixels — which is the
 * whole visible difference between this field and the original's flat one.
 */
function starGeometry(stars: readonly PlacedNightStar[]): THREE.BufferGeometry {
  const positions = new Float32Array(stars.length * 4 * 3);
  const colours = new Float32Array(stars.length * 4 * 3);
  const indices: number[] = [];
  const fovY = (CAMERA_FOV_DEG * Math.PI) / 180;
  const pixelWidthM = metresPerScreenPixelX(CELESTIAL_DISTANCE_M, fovY);
  const pixelHeightM = metresPerScreenPixel(CELESTIAL_DISTANCE_M, fovY);
  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  const bearing = new THREE.Vector3();

  stars.forEach((star, index) => {
    const base = index * 4;
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    bearing.set(star.direction.x, star.direction.y, star.direction.z);
    // Any two axes perpendicular to the bearing will do; world up is only a
    // seed for the cross product and a star straight overhead gets `+X` instead.
    right.set(0, 1, 0).cross(bearing);
    if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
    right.normalize();
    up.copy(bearing).cross(right).normalize();

    const halfWidth = (star.sizePx * pixelWidthM) / 2;
    const halfHeight = (star.sizePx * pixelHeightM) / 2;
    const corners: readonly (readonly [number, number])[] = [
      [-halfWidth, -halfHeight],
      [halfWidth, -halfHeight],
      [halfWidth, halfHeight],
      [-halfWidth, halfHeight],
    ];
    corners.forEach(([x, y], corner) => {
      const at = (base + corner) * 3;
      positions[at] = bearing.x * CELESTIAL_DISTANCE_M + right.x * x + up.x * y;
      positions[at + 1] = bearing.y * CELESTIAL_DISTANCE_M + right.y * x + up.y * y;
      positions[at + 2] = bearing.z * CELESTIAL_DISTANCE_M + right.z * x + up.z * y;
      colours[at] = ((star.colour >> 16) & 0xff) / 255;
      colours[at + 1] = ((star.colour >> 8) & 0xff) / 255;
      colours[at + 2] = (star.colour & 0xff) / 255;
    });
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colours, 3));
  geometry.setIndex(indices);
  return geometry;
}

/** The star field as one draw call. Empty input builds nothing, so a moonless overcast sky costs no mesh. */
export function createCatalogueStarField(stars: readonly PlacedNightStar[]): THREE.Mesh | undefined {
  if (stars.length === 0) return undefined;
  const mesh = new THREE.Mesh(
    starGeometry(stars),
    // `DoubleSide` because a quad's winding about its own bearing is whichever
    // way the cross products fell, and a star is a dot either way.
    new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, fog: false }),
  );
  mesh.renderOrder = STAR_RENDER_ORDER;
  // Its bounding sphere is recentred as the renderer draws it, which is after
  // the frustum test would have read the stale one — `createSky`'s hazard.
  mesh.frustumCulled = false;
  mesh.onBeforeRender = (_renderer, _scene, camera) => {
    camera.getWorldPosition(mesh.position);
    mesh.updateMatrixWorld();
  };
  return mesh;
}

/**
 * The moon's phase, as a shader.
 *
 * The terminator is the projection of the day/night circle onto the disc, and
 * that projection is an **ellipse sharing the disc's own limb**: in coordinates
 * where `u` runs along the bright limb's direction, the lit side is everything
 * with `u > -cos(i) * sqrt(1 - v²)`, `i` being the phase angle. Check the three
 * ends of it — `i = 0` puts the boundary on the dark limb (a full moon),
 * `i = 90` puts it on the diameter (a half moon), `i = 180` puts it on the
 * bright limb (a new moon) — and the single `cos` is the whole of the phase.
 *
 * `limb` is a **uniform** rather than a constant because the bright limb's
 * direction on screen depends on the camera's roll: the same gibbous moon is
 * lit on its left in level flight and on its top upside down, and getting that
 * wrong is the one error in this whole feature that a pilot would see every
 * frame.
 */
function moonMaterial(lit: number, dark: number, limb: THREE.Vector2, cosPhase: number): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ fog: false });
  const limbNode = THREE.TSL.uniform(limb);
  const disc = THREE.TSL.positionGeometry.xy.div(DISC_RADIUS);
  const along = THREE.TSL.dot(disc, limbNode);
  const across = THREE.TSL.dot(disc, THREE.TSL.vec2(limbNode.y.negate(), limbNode.x));
  const edge = THREE.TSL.sqrt(across.mul(across).oneMinus().max(0));
  const terminator = edge.mul(-cosPhase);
  const daylight = THREE.TSL.smoothstep(
    terminator.sub(TERMINATOR_SOFTNESS),
    terminator.add(TERMINATOR_SOFTNESS),
    along,
  );
  material.colorNode = quantiseToVgaDac(THREE.TSL.mix(paletteVec3(dark), paletteVec3(lit), daylight));
  material.userData.limb = limbNode;
  return material;
}

/** A palette entry as the shader wants it — see `world.ts`'s note on colour management. */
function paletteVec3(colour: number): THREE.Node<"vec3"> {
  return THREE.TSL.vec3(((colour >> 16) & 0xff) / 255, ((colour >> 8) & 0xff) / 255, (colour & 0xff) / 255);
}

const TEMP_BEARING = new THREE.Vector3();
const TEMP_SUN = new THREE.Vector3();
const TEMP_RIGHT = new THREE.Vector3();
const TEMP_UP = new THREE.Vector3();

/**
 * The moon: `SUN.BMP` frame 1's own 17-of-200-scanlines disc, in `BARON.PAL`
 * 199, with the terminator cut into it.
 *
 * Deliberately the **same size and the same colour** as the authentic disc it
 * stands in for. The claim ENHANCED makes at night is that the moon is in the
 * right place and the right phase, not that it is a different object — and the
 * original's disc is eight times the real moon's half-degree, which is exactly
 * why a phase is legible on it at all.
 *
 * `dark` is the unlit half. It is not black: a gibbous moon's dark limb is
 * lit by earthshine, and painting it a tenth of the way from the sky toward
 * the moon's own grey is both what that looks like and what keeps the disc
 * from reading as a hole punched in the sky.
 */
export function createMoon(moon: PlacedMoon, stops: EnhancedSkyStops): THREE.Mesh {
  const limb = new THREE.Vector2(1, 0);
  const dark = mixPalette(stops.mid, MOON_DISC.colour, 0.1);
  const cosPhase = Math.cos((moon.phase.phaseAngleDeg * Math.PI) / 180);
  const mesh = new THREE.Mesh(discGeometry(64), moonMaterial(MOON_DISC.colour, dark, limb, cosPhase));
  mesh.renderOrder = CELESTIAL_RENDER_ORDER;
  mesh.frustumCulled = false;
  mesh.onBeforeRender = (_renderer, _scene, camera) => placeMoon(mesh, moon, limb, camera);
  return mesh;
}

/**
 * Puts the moon a fixed distance along its bearing, squares it to the lens,
 * sizes it by the original's blit rule (`sprite-screen-size.ts`, the same
 * inversion `sky-dressing.ts` uses rather than a second copy of it), and
 * points the bright limb at the sun.
 *
 * The limb is found geometrically rather than from a position angle: the sun
 * is far enough away that the lit side simply faces it, so the component of
 * the sun's bearing perpendicular to the moon's bearing, resolved onto the
 * camera's own right and up axes, *is* the limb direction on screen. That
 * needs no parallactic angle and cannot disagree with where the two bodies are
 * actually drawn.
 */
function placeMoon(mesh: THREE.Mesh, moon: PlacedMoon, limb: THREE.Vector2, camera: THREE.Camera): void {
  const facing = placeCelestialDisc(mesh, moon.direction, MOON_DISC, camera);
  const bearing = TEMP_BEARING.set(moon.direction.x, moon.direction.y, moon.direction.z).normalize();
  const sun = TEMP_SUN.set(moon.sunDirection.x, moon.sunDirection.y, moon.sunDirection.z).normalize();
  sun.addScaledVector(bearing, -sun.dot(bearing));
  const right = TEMP_RIGHT.set(1, 0, 0).applyQuaternion(facing);
  const up = TEMP_UP.set(0, 1, 0).applyQuaternion(facing);
  // A sun exactly behind or exactly in front of the moon leaves nothing to
  // project; the disc is then full or new and the limb's direction does not
  // change a pixel of it, so any unit vector will do.
  if (sun.lengthSq() < 1e-8) limb.set(1, 0);
  else limb.set(sun.dot(right), sun.dot(up)).normalize();
}

/**
 * The group's `name`, so a layer that has to *hide* the night sky can find it
 * without being handed a reference. `sky-dressing.ts`'s overcast deck is the
 * one that needs it — stars through solid cloud is the one thing an overcast
 * sky cannot show — and the tests read it for the same reason.
 */
export const NIGHT_SKY_GROUP = "night-sky";

/** The whole night layer: the field, the moon, and the bearing the dome hangs its halo on. */
export interface NightSkyLayer {
  readonly group: THREE.Group;
  readonly sky: NightSky;
}

/**
 * Builds both meshes for one brief. Nothing here fetches, so unlike the baked
 * star table there is no window in which the sky is empty — the catalogue is
 * in the bundle (`bright-stars.ts` says why).
 *
 * `stops` are the **greyed** ones (`greyedStops`), the same the dome is painted
 * from, so a star is blended out of exactly the colour that is behind it and a
 * cloudy night dims the field and the sky together.
 */
export function createNightSky(brief: NightSkyBrief, stops: EnhancedSkyStops, greyMix: number): NightSkyLayer {
  const sky = nightSkyFor(brief, {
    zenith: stops.zenith,
    mid: stops.mid,
    horizon: stops.horizonCool,
    midElevationDeg: stops.midElevationDeg,
    hazeElevationDeg: stops.hazeElevationDeg,
    limitMagnitude: limitMagnitudeFor(greyMix),
  });
  const group = new THREE.Group();
  group.name = NIGHT_SKY_GROUP;
  const field = createCatalogueStarField(sky.stars);
  if (field) group.add(field);
  if (sky.moon) group.add(createMoon(sky.moon, stops));
  return { group, sky };
}
